// The in-memory twin of the TEMPLATES-T2..T4 company routes (services/api/src/routes/
// role-templates.ts). The fake catalog publishes no role template and no vertical pack, so the
// screens show their honest empty states: the role list is empty, a plan or an apply is 404, and
// a DE has no role access and no probation. The company's overlays are kept per company (create,
// edit with If-Match, archive), the same rules as the server's: managers only, a 409 on a stale
// version or a second live company overlay for the same role.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";

type Overlay = OperationOutput<"templateOverlays.list">["data"][number];
type Create = OperationInput<"templateOverlays.create">["body"];
type Patch = OperationInput<"templateOverlays.update">["body"];

export const FAKE_ROLE_TEMPLATE_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "roleTemplates.list",
  "roleTemplates.plan",
  "templateOverlays.list",
  "templateOverlays.create",
  "templateOverlays.update",
  "templateOverlays.archive",
  "verticalPacks.apply",
  "digitalEmployees.applyRole",
  "digitalEmployees.access",
  "digitalEmployees.putAccess",
  "digitalEmployees.endProbation",
]);

const forbidden = () => problem(403, "forbidden", "Forbidden", "Managers only");
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);

export function createRoleTemplatesFake(deps: {
  now: () => Date;
  /** Is this a DE of the caller's company? */
  isDe: (p: Principal, id: string) => boolean;
}): Partial<Record<RouteId, Handler>> {
  const overlays = new Map<string, Overlay[]>();
  const of = (p: Principal) => {
    const list = overlays.get(p.company.id) ?? [];
    overlays.set(p.company.id, list);
    return list;
  };
  const actorOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const versionOf = (headers: Headers) => {
    const m = /"?(\d+)"?/.exec(headers.get("if-match") ?? "");
    return m ? Number(m[1]) : null;
  };
  const guard = (p: Principal | null): Response | Principal =>
    !p ? notFound() : !p.manager ? forbidden() : p;

  return {
    "roleTemplates.list": ({ principal }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      return json({ data: [], vertical_packs: [] });
    },
    "roleTemplates.plan": ({ principal }) => {
      const p = guard(principal);
      return p instanceof Response ? p : notFound();
    },
    "verticalPacks.apply": ({ principal }) => {
      const p = guard(principal);
      return p instanceof Response ? p : notFound();
    },
    "digitalEmployees.applyRole": ({ principal }) => {
      const p = guard(principal);
      return p instanceof Response ? p : notFound();
    },
    "digitalEmployees.access": ({ principal, params }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      if (!deps.isDe(p, params.id ?? "")) return notFound();
      return json({ data: [], caps: {}, probation: null });
    },
    "digitalEmployees.putAccess": ({ principal, params }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      if (!deps.isDe(p, params.id ?? "")) return notFound();
      return conflict("This digital employee was not set up from a role.");
    },
    "digitalEmployees.endProbation": ({ principal, params }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      if (!deps.isDe(p, params.id ?? "")) return notFound();
      return conflict("This digital employee is not on probation.");
    },
    "templateOverlays.list": ({ principal, url }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      const archived = url.searchParams.get("archived") === "true";
      return json({ data: of(p).filter((o) => archived || o.archived_at === null) });
    },
    "templateOverlays.create": ({ principal, body }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      const b = body as Create | null;
      if (!b || typeof b.name !== "string" || !b.name.trim()) return validation("name: required");
      const key = b.template_key ?? null;
      if (of(p).some((o) => o.scope === "company" && o.template_key === key && !o.archived_at))
        return conflict("The company already has an overlay for this.");
      const at = deps.now().toISOString();
      const o: Overlay = {
        company_id: p.company.id,
        id: crypto.randomUUID(),
        scope: "company",
        template_key: key,
        vertical_pack_id: null,
        name: b.name.trim(),
        context_text: b.context_text ?? null,
        tone_text: b.tone_text ?? null,
        add_skill_keys: b.add_skill_keys ?? [],
        block_skill_keys: b.block_skill_keys ?? [],
        access_caps: b.access_caps ?? {},
        blocked_actions: b.blocked_actions ?? [],
        kpi_overrides: b.kpi_overrides ?? {},
        model_tier: b.model_tier ?? null,
        budget_minor: b.budget_minor ?? null,
        version: 1,
        archived_at: null,
        actor_kind: "person",
        actor_membership_id: actorOf(p),
        created_at: at,
        updated_at: at,
      };
      of(p).push(o);
      return json(o, 201);
    },
    "templateOverlays.update": ({ principal, params, body, headers }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      const list = of(p);
      const i = list.findIndex((o) => o.id === params.id);
      const cur = list[i];
      if (!cur) return notFound();
      const v = versionOf(headers);
      if (v === null) return problem(428, "precondition_required", "If-Match required");
      if (v !== cur.version) return conflict("The overlay changed since it was read.");
      const b = (body ?? {}) as Patch;
      const next: Overlay = {
        ...cur,
        ...Object.fromEntries(Object.entries(b).filter(([, x]) => x !== undefined)),
        name: typeof b.name === "string" ? b.name.trim() : cur.name,
        version: cur.version + 1,
        updated_at: deps.now().toISOString(),
      } as Overlay;
      list[i] = next;
      return json(next);
    },
    "templateOverlays.archive": ({ principal, params, headers }) => {
      const p = guard(principal);
      if (p instanceof Response) return p;
      const list = of(p);
      const i = list.findIndex((o) => o.id === params.id);
      const cur = list[i];
      if (!cur) return notFound();
      const v = versionOf(headers);
      if (v === null) return problem(428, "precondition_required", "If-Match required");
      if (v !== cur.version) return conflict("The overlay changed since it was read.");
      const at = deps.now().toISOString();
      const next: Overlay = { ...cur, archived_at: at, updated_at: at, version: cur.version + 1 };
      list[i] = next;
      return json(next);
    },
  };
}
